import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { DnaBackground } from "@/components/DnaBackground";
import { PasswordInput } from "@/components/PasswordInput";
import { PasswordStrengthIndicator } from "@/components/PasswordStrengthIndicator";
import { useTranslation } from "react-i18next";
import { authAPI } from "@/lib/api";
import { calculatePasswordStrength, getPasswordRequirements } from "@/lib/password-validation";
import { toast } from "sonner";
import { CheckCircle2, Loader2, UserCheck } from "lucide-react";
import { getLocalizedErrorMessage } from "@/lib/error-i18n";

export default function ActivateAccount() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const passwordStrength = calculatePasswordStrength(password, t);
  const passwordRequirements = getPasswordRequirements(password, t);

  const activateAccountMutation = useMutation({
    mutationFn: ({ activationToken, newPassword }: { activationToken: string; newPassword: string }) =>
      authAPI.activateAccount(activationToken, newPassword),
    onSuccess: () => {
      toast.success(t("activateAccount.success"));
    },
  });

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (password !== confirmPassword) {
      toast.error(t("auth.passwordsMismatch"));
      return;
    }

    if (password.length < 6) {
      toast.error(t("auth.passwordTooShort"));
      return;
    }

    if (!/[A-Z]/.test(password)) {
      toast.error(t("auth.passwordNeedsUppercase"));
      return;
    }

    if (!/\d/.test(password)) {
      toast.error(t("auth.passwordNeedsNumber"));
      return;
    }

    if (!/[^A-Za-z0-9]/.test(password)) {
      toast.error(t("auth.passwordNeedsSpecial"));
      return;
    }

    if (!token) {
      toast.error(t("activateAccount.invalidToken"));
      return;
    }

    activateAccountMutation.mutate({ activationToken: token, newPassword: password });
  };

  if (activateAccountMutation.isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 relative">
        <DnaBackground />

        <Card className="w-full max-w-md p-8 bg-card border-border shadow-glow animate-scale-in">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-500" />
            </div>

            <h1 className="text-3xl font-bold text-gradient mb-4">
              {t("activateAccount.successTitle")}
            </h1>

            <p className="text-muted-foreground mb-6">
              {t("activateAccount.successMessage")}
            </p>

            <Button
              onClick={() => navigate("/auth")}
              className="w-full bg-primary hover:bg-primary/90 shadow-glow"
            >
              {t("activateAccount.goToSignIn")}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative">
      <DnaBackground />

      <Card className="w-full max-w-md p-8 bg-card border-border shadow-glow animate-scale-in">
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <UserCheck className="w-8 h-8 text-primary" />
          </div>

          <h1 className="text-3xl font-bold text-gradient mb-2">
            {t("activateAccount.title")}
          </h1>

          <p className="text-muted-foreground">
            {t("activateAccount.description")}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <PasswordInput
            id="password"
            label={t("activateAccount.newPassword")}
            value={password}
            onChange={setPassword}
            placeholder="••••••••"
            required
            autoComplete="new-password"
          />

          {password && (
            <PasswordStrengthIndicator
              strength={passwordStrength}
              requirements={passwordRequirements}
              strengthLabel={t("auth.passwordStrength")}
            />
          )}

          <PasswordInput
            id="confirmPassword"
            label={t("auth.confirmPassword")}
            value={confirmPassword}
            onChange={setConfirmPassword}
            placeholder="••••••••"
            required
            autoComplete="new-password"
          />

          {activateAccountMutation.isError && (
            <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md text-start">
              {getLocalizedErrorMessage(activateAccountMutation.error, "errors.authentication")}
            </div>
          )}

          <Button
            type="submit"
            className="w-full bg-primary hover:bg-primary/90 shadow-glow"
            disabled={activateAccountMutation.isPending}
          >
            {activateAccountMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t("activateAccount.activating")}
              </>
            ) : (
              t("activateAccount.activateButton")
            )}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => navigate("/auth")}
            className="text-sm text-primary hover:underline"
          >
            {t("forgotPassword.backToSignIn")}
          </button>
        </div>
      </Card>
    </div>
  );
}
